import styled from "@emotion/styled";
import classNames from "classnames";
import { get } from "es-toolkit/compat";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
} from "react";

import { DropIndicator, SkiliticsIcon, zIndexes } from "@skilitics/designer-ui-components";
import type { Data, UniqueIdentifier } from "@skilitics/drag-drop";
import { SortableKeyboardPlugin, useSortable } from "@skilitics/drag-drop";

import { GROUP_CONDITION_NAME, NEGATION_CONDITION_NAME, TYPE_CANCEL, TYPE_DROPPABLE } from "../constants";
import { DROPZONE_PADDING } from "../editors/constants";
import { EditButtonContainer } from "../editors/shared";
import { droppableDataSchema } from "./dndSchemas";

const DragIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  width: ${DROPZONE_PADDING}px;
`;

const SortableItemContainer = styled.div`
  display: flex;
  position: relative;
  max-width: 100%;

  &.hovered {
    cursor: grab;
    > ${DragIcon} {
      opacity: 1;
    }
    > div > ${EditButtonContainer} {
      opacity: 1;
    }
  }

  &.is-dragging {
    cursor: grabbing;
    opacity: 0.4;
  }
`;

type SortableInstance = { sortable?: { index: number } };

export type SortableItemProps = HTMLAttributes<HTMLDivElement> & {
  id: UniqueIdentifier;
  index: number;
  group?: UniqueIdentifier;
  data?: Data;
  children: ReactNode;
  accepts?: Record<string, string[]>;
  disabled?: boolean;
  color?: "primary" | "secondary" | "warning";
};

export const SortableItem = ({
  id,
  index,
  group,
  data,
  children,
  disabled,
  accepts,
  color = "secondary",
  ...domAttrs
}: SortableItemProps) => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const {
    isDragging,
    isDropTarget,
    sortable: { manager, index: sortableIndex },
  } = useSortable({
    element: ref,
    id,
    index,
    group,
    data,
    disabled,
    plugins: [SortableKeyboardPlugin],
  });

  const source = manager?.dragOperation?.source;
  const target = manager?.dragOperation?.target;

  const allowedOver = useMemo(() => {
    if (!disabled) {
      if (!accepts) return true; // accepts not defined, so accepts everything.

      for (const key of Object.keys(accepts)) {
        const activeData = get(source?.data, key);
        if (activeData && (get(accepts, key) ?? []).includes(activeData)) {
          return true;
        }
      }
    }
    return false;
  }, [disabled, accepts, source?.data]);

  const insertPosition = useMemo(() => {
    if (!target || !isDropTarget || !source || !allowedOver) return;
    const { rule: overRule } = droppableDataSchema.parse(target.data);
    if (overRule.type === TYPE_CANCEL) return;

    if (
      overRule.type === TYPE_DROPPABLE &&
      (overRule.typeId === GROUP_CONDITION_NAME || overRule.typeId === NEGATION_CONDITION_NAME)
    ) {
      return "inside";
    }

    const sourceIndex = (source as SortableInstance).sortable?.index;
    if (sourceIndex !== undefined) {
      return sourceIndex < sortableIndex ? "insertBottom" : "insertTop";
    }
  }, [source, target, isDropTarget, allowedOver, sortableIndex]);

  const handleMouseOver: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
    e.stopPropagation();
    setHovered(true);
  }, []);

  const handleMouseOut: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
    e.stopPropagation();
    setHovered(false);
  }, []);

  return (
    <SortableItemContainer
      ref={ref}
      className={classNames({
        "is-dragging": isDragging,
        hovered: hovered,
      })}
      data-test="sortableItem"
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      {...domAttrs}
    >
      <DragIcon>
        <SkiliticsIcon icon="drag-grip" />
      </DragIcon>
      {children}
      {insertPosition && insertPosition !== "inside" && (
        <DropIndicator
          insertPosition={insertPosition}
          color={color}
          domRef={ref}
          offset={{ top: insertPosition === "insertTop" ? 10 : -10, left: 0 }}
          style={{ position: "fixed", zIndex: zIndexes.triggersOverlayPopover + 2 }}
        />
      )}
    </SortableItemContainer>
  );
};
